import React, {
	createContext,
	FC,
	ReactNode,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { callLead, callUser, getMyAgentMapping } from '../apis';
import Button from '../components/bootstrap/Button';
import Modal, {
	ModalBody,
	ModalFooter,
	ModalHeader,
	ModalTitle,
} from '../components/bootstrap/Modal';
import Checks from '../components/bootstrap/forms/Checks';
import AuthContext from './authContext';

interface IMyAgentMapping {
	id?: string | number;
	employeeUserId?: string | number;
	agentId?: string;
	allowedCallerIds?: string[] | null;
	defaultCallerId?: string | null;
}

export interface IClickToCallResult {
	cancelled: boolean;
	data?: any;
}

interface IClickToCallContext {
	initiateLeadCall: (leadId: number) => Promise<IClickToCallResult>;
	initiateUserCall: (userId: number) => Promise<IClickToCallResult>;
}

interface IClickToCallProviderProps {
	children: ReactNode;
}

interface ICallerIdSelection {
	cancelled: boolean;
	callerId?: string;
	errorData?: any;
}

const ClickToCallContext = createContext<IClickToCallContext | undefined>(undefined);

const normalizeCallerIds = (mapping: IMyAgentMapping | undefined): string[] => {
	const rawCallerIds = mapping?.allowedCallerIds;
	if (!Array.isArray(rawCallerIds)) {
		return [];
	}

	return Array.from(
		new Set(
			rawCallerIds.map((callerId) => String(callerId || '').trim()).filter(Boolean),
		),
	);
};

const extractMapping = (response: any): IMyAgentMapping | undefined => {
	if (Array.isArray(response?.data)) {
		return response.data[0];
	}
	return response?.data && typeof response.data === 'object' ? response.data : undefined;
};

const isLegacyMappingEndpointUnavailable = (response: any): boolean =>
	response?.legacyAgentMappingEndpointUnavailable === true &&
	[404, 405].includes(response?.httpStatus);

export const ClickToCallProvider: FC<IClickToCallProviderProps> = ({ children }) => {
	const { userData } = useContext(AuthContext);
	const [selectorOpen, setSelectorOpen] = useState(false);
	const [callerIds, setCallerIds] = useState<string[]>([]);
	const [selectedCallerId, setSelectedCallerId] = useState('');
	const pendingSelectionRef = useRef<((selection: ICallerIdSelection) => void) | null>(null);
	const selectionGenerationRef = useRef(0);

	const finishSelection = useCallback((selection: ICallerIdSelection) => {
		const resolve = pendingSelectionRef.current;
		pendingSelectionRef.current = null;
		setSelectorOpen(false);
		setCallerIds([]);
		setSelectedCallerId('');
		resolve?.(selection);
	}, []);

	const chooseCallerId = useCallback(async (): Promise<ICallerIdSelection> => {
		const selectionGeneration = ++selectionGenerationRef.current;
		if (pendingSelectionRef.current) {
			finishSelection({ cancelled: true });
		}

		try {
			const response = await getMyAgentMapping(
				process.env.REACT_APP_ARKAPIURL,
				userData.token,
			);
			if (selectionGeneration !== selectionGenerationRef.current) {
				return { cancelled: true };
			}
			if (isLegacyMappingEndpointUnavailable(response)) {
				// Older servers do not expose the self-mapping endpoint, but their call
				// endpoints still support the provider-configured default caller ID.
				return { cancelled: false };
			}
			if (
				response === 'sessionExpired' ||
				response?.success !== true ||
				response?.status === false
			) {
				return {
					cancelled: false,
					errorData: response ?? {
						success: false,
						message: 'Unable to load the allowed outbound caller IDs.',
					},
				};
			}
			const mapping = extractMapping(response);
			if (!mapping) {
				return {
					cancelled: false,
					errorData: {
						success: false,
						message: 'No click-to-call mapping found for this logged-in employee.',
					},
				};
			}
			const availableCallerIds = normalizeCallerIds(mapping);

			if (!availableCallerIds.length) {
				return { cancelled: false };
			}
			if (availableCallerIds.length === 1) {
				return { cancelled: false, callerId: availableCallerIds[0] };
			}

			const configuredDefault = String(mapping?.defaultCallerId || '').trim();
			setCallerIds(availableCallerIds);
			setSelectedCallerId(
				configuredDefault && availableCallerIds.includes(configuredDefault)
					? configuredDefault
					: availableCallerIds[0],
			);
			setSelectorOpen(true);
			return await new Promise<ICallerIdSelection>((resolve) => {
				pendingSelectionRef.current = resolve;
			});
		} catch (error) {
			if (selectionGeneration !== selectionGenerationRef.current) {
				return { cancelled: true };
			}
			return {
				cancelled: false,
				errorData: {
					success: false,
					status: false,
					message:
						error instanceof Error
							? error.message
							: 'Unable to load the allowed outbound caller IDs.',
				},
			};
		}
	}, [finishSelection, userData.token]);

	const initiateLeadCall = useCallback(
		async (leadId: number): Promise<IClickToCallResult> => {
			const selection = await chooseCallerId();
			if (selection.cancelled) {
				return { cancelled: true };
			}
			if (selection.errorData !== undefined) {
				return { cancelled: false, data: selection.errorData };
			}
			const data = await callLead(
				process.env.REACT_APP_ARKAPIURL || '',
				leadId,
				userData.token,
				selection.callerId,
			);
			return { cancelled: false, data };
		},
		[chooseCallerId, userData.token],
	);

	const initiateUserCall = useCallback(
		async (userId: number): Promise<IClickToCallResult> => {
			const selection = await chooseCallerId();
			if (selection.cancelled) {
				return { cancelled: true };
			}
			if (selection.errorData !== undefined) {
				return { cancelled: false, data: selection.errorData };
			}
			const data = await callUser(
				process.env.REACT_APP_ARKAPIURL || '',
				userId,
				userData.token,
				selection.callerId,
			);
			return { cancelled: false, data };
		},
		[chooseCallerId, userData.token],
	);

	useEffect(
		() => () => {
			selectionGenerationRef.current += 1;
			pendingSelectionRef.current?.({ cancelled: true });
			pendingSelectionRef.current = null;
		},
		[userData.id],
	);

	const value = useMemo(
		() => ({ initiateLeadCall, initiateUserCall }),
		[initiateLeadCall, initiateUserCall],
	);

	return (
		<ClickToCallContext.Provider value={value}>
			{children}
			<Modal
				isOpen={selectorOpen}
				setIsOpen={(isOpen: boolean) => {
					if (!isOpen) {
						finishSelection({ cancelled: true });
					}
				}}
				titleId='clickToCallMobileSelector'
				isCentered
				isStaticBackdrop>
				<ModalHeader setIsOpen={() => finishSelection({ cancelled: true })}>
					<ModalTitle id='clickToCallMobileSelector'>Choose outbound number</ModalTitle>
				</ModalHeader>
				<ModalBody>
					<p className='text-muted mb-3'>
						Select the caller ID/mobile number the customer should see.
					</p>
					<div className='d-grid gap-2'>
						{callerIds.map((callerId, index) => (
							<label
								key={callerId}
								htmlFor={`clickToCallMobile-${index}`}
								className={`border rounded-3 p-3 d-flex align-items-center gap-3 cursor-pointer ${
									selectedCallerId === callerId
										? 'border-primary bg-primary bg-opacity-10'
										: ''
								}`}>
								<Checks
									id={`clickToCallMobile-${index}`}
									name='clickToCallMobile'
									type='radio'
									value={callerId}
									checked={selectedCallerId}
									onChange={() => setSelectedCallerId(callerId)}
									isFormCheckInput
								/>
								<span className='fw-semibold'>{callerId}</span>
							</label>
						))}
					</div>
				</ModalBody>
				<ModalFooter>
					<Button
						color='info'
						isOutline
						onClick={() => finishSelection({ cancelled: true })}>
						Cancel
					</Button>
					<Button
						color='primary'
						isDisable={!selectedCallerId}
						onClick={() =>
							finishSelection({ cancelled: false, callerId: selectedCallerId })
						}>
						Call from this number
					</Button>
				</ModalFooter>
			</Modal>
		</ClickToCallContext.Provider>
	);
};

export const useClickToCall = (): IClickToCallContext => {
	const context = useContext(ClickToCallContext);
	if (!context) {
		throw new Error('useClickToCall must be used within ClickToCallProvider.');
	}
	return context;
};

export default ClickToCallContext;
